import Component from '@glimmer/component'
import config from 'teacher-ui-client/config/environment'
import { service } from '@ember/service'
import type SessionService from 'teacher-ui-client/services/session'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type PartnerService from 'teacher-ui-client/services/partner'
import { PartnerCode } from 'teacher-ui-client/partner/partners'
import type PromoService from 'teacher-ui-client/services/promo'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Owner from '@ember/owner'
import type SchoolSubscription from 'teacher-ui-client/models/school-subscription'
import { format } from 'date-fns'

/**
 * This component is responsible for adding the script which loads our
 * WalkMe tours for users.
 *
 * Can be enabled/disabled via config.walkme or in the getter below.
 */

function sortSubscriptionsByStartDate(subscriptions: Array<SchoolSubscription>) {
  const sortedSubs = subscriptions.sort((a, b) => {
    // put sub that has the latest start date first
    if (b.startDate > a.startDate) return 1
    if (b.startDate < a.startDate) return -1
    // if the same start date, put one with the further endDate
    else {
      if (b.endDate > a.endDate) return 1
      if (b.endDate < a.endDate) return -1
      // otherwsie return alphabetically
      return a.subscriptionName.localeCompare(b.subscriptionName)
    }
  })
  return sortedSubs
}

export interface ActiveSubscriptionDateRange {
  start_date: string
  end_date: string
}

export type ActiveSubscriptions = Partial<Record<SubscriptionType, ActiveSubscriptionDateRange>>

// Mimicking the logic in roster, we get the earliest start date and latest end date across all provided active subscriptions.
function getActiveSubscriptionDateRange(
  activeSubscriptions: Array<SchoolSubscription>,
): ActiveSubscriptionDateRange | undefined {
  if (!activeSubscriptions.length) return undefined

  const { earliest, latest } = activeSubscriptions.slice(1).reduce(
    (range, subscription) => {
      if (
        subscription.startDate < range.earliest.startDate ||
        (subscription.startDate.getTime() === range.earliest.startDate.getTime() &&
          subscription.endDate > range.earliest.endDate)
      ) {
        range.earliest = subscription
      }

      if (
        subscription.endDate > range.latest.endDate ||
        (subscription.endDate.getTime() === range.latest.endDate.getTime() &&
          subscription.startDate < range.latest.startDate)
      ) {
        range.latest = subscription
      }

      return range
    },
    {
      earliest: activeSubscriptions[0],
      latest: activeSubscriptions[0],
    },
  )

  return {
    start_date: format(earliest.startDate, 'M-yyyy'),
    end_date: format(latest.endDate, 'M-yyyy'),
  }
}

// Unlike getActiveSubscriptionDateRange, this picks the single subscription with the furthest-out end date,
// and returns its own start/end dates, rather than an aggregated range.
function getFurthestOutSubscriptionDateRange(
  subscriptions: Array<SchoolSubscription>,
): ActiveSubscriptionDateRange | undefined {
  if (!subscriptions.length) return undefined

  const furthestOut = subscriptions.slice(1).reduce((furthest, subscription) => {
    if (
      subscription.endDate > furthest.endDate ||
      (subscription.endDate.getTime() === furthest.endDate.getTime() && subscription.startDate < furthest.startDate)
    ) {
      return subscription
    }
    return furthest
  }, subscriptions[0])

  return {
    start_date: format(furthestOut.startDate, 'M-yyyy'),
    end_date: format(furthestOut.endDate, 'M-yyyy'),
  }
}

export class WalkMe extends Component {
  constructor(owner: Owner, args: never) {
    super(owner, args)
    this.setGlobals()
  }

  @service partner!: PartnerService

  @service session!: SessionService

  @service subscriptionType!: SubscriptionTypeService

  @service promo!: PromoService

  get shouldLoadWalkMe() {
    if (this.session.currentSchool.isHomeschool) {
      return false
    }
    return config.walkme.enabled
  }

  get loadScript() {
    return config.walkme.loadScript
  }

  setGlobals() {
    // avoid recreating these properties if they've already been defined.
    if (Object.prototype.hasOwnProperty.call(window, 'countryCode')) {
      return
    }

    const subscriptionTypeService = this.subscriptionType
    const sessionService = this.session
    const partnerService = this.partner
    const promoService = this.promo

    window._walkmeConfig = { smartLoad: true }

    // @deprecated DO NOT ADD MORE PROPERTIES HERE, USE THE WALKME OBJECTS BELOW
    Object.defineProperties(window, {
      countryCode: {
        configurable: true,
        get() {
          // WalkMe is expecting country codes as upper case
          return sessionService.currentSchool.countryCode
        },
      },
      isCleverManaged: {
        configurable: true,
        get() {
          return sessionService.currentSchool.externalRosteringSource === 'classlink'
        },
      },
      isPartnerManaged: {
        configurable: true,
        get() {
          const currentSubscription = subscriptionTypeService.current
          if (!currentSubscription) return false
          return !partnerService.hasPartner(PartnerCode._3P, currentSubscription)
        },
      },
      isTeacherTrial: {
        configurable: true,
        get() {
          return (
            subscriptionTypeService.current &&
            sessionService.currentTeacher.hasTrialSubscriptionFor(subscriptionTypeService.current)
          )
        },
      },
      partner: {
        configurable: true,
        get() {
          const currentSubscription = subscriptionTypeService.current

          if (!currentSubscription) return

          return partnerService.partnerForSubscriptionType(currentSubscription)?.code
        },
      },
      subscriptionType: {
        configurable: true,
        get() {
          return subscriptionTypeService.current
        },
      },
      userType: {
        configurable: true,
        get() {
          if (!subscriptionTypeService.current) return
          // WalkMe is expecting Teacher vs teacher
          return sessionService.currentTeacher.subcoFor(subscriptionTypeService.current) ? 'Subco' : 'Teacher'
        },
      },
      ms_us_cross_sell: {
        configurable: true,
        get() {
          return promoService.shouldShowMSUpsell
        },
      },
      reading_subscription: {
        configurable: true,
        get() {
          const readingSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) => subscription.subscriptionType === SubscriptionType.Reading && subscription.isActive,
          )
          return Boolean(readingSubscription)
        },
      },
      maths_subscription: {
        configurable: true,
        get() {
          const mathsSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) => subscription.subscriptionType === SubscriptionType.Maths && subscription.isActive,
          )
          return Boolean(mathsSubscription)
        },
      },
      writing_subscription: {
        configurable: true,
        get() {
          const writingSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) => subscription.subscriptionType === SubscriptionType.Writing && subscription.isActive,
          )
          return Boolean(writingSubscription)
        },
      },
      reading_teacher_trial: {
        configurable: true,
        get() {
          return (
            sessionService.currentTeacher
              .teacherSubscriptionsFor(SubscriptionType.Reading)
              ?.some((sub) => sub.isActive) ?? false
          )
        },
      },
      maths_teacher_trial: {
        configurable: true,
        get() {
          return (
            sessionService.currentTeacher
              .teacherSubscriptionsFor(SubscriptionType.Maths)
              ?.some((sub) => sub.isActive) ?? false
          )
        },
      },
      writing_teacher_trial: {
        configurable: true,
        get() {
          return (
            sessionService.currentTeacher
              .teacherSubscriptionsFor(SubscriptionType.Writing)
              ?.some((sub) => sub.isActive) ?? false
          )
        },
      },
      school_state: {
        configurable: true,
        get() {
          return sessionService.currentSchool.state
        },
      },

      promoSubscriptionName: {
        configurable: true,
        get() {
          const promoSubNames = ['ms_free_access_nov_24', 're_free_access_nov_24']

          const activeSubs = sessionService.currentSchool.schoolSubscriptions.filter(
            (subscription) => subscription.isActive,
          )

          // Return promo subscription names if active - regardless of current tab
          const promoSub = activeSubs.find((subscription) => promoSubNames.includes(subscription.subscriptionName))
          if (promoSub) return promoSub.subscriptionName
          return false
        },
      },

      currentSubscriptionName: {
        configurable: true,
        get() {
          const activeSubs = sessionService.currentSchool.schoolSubscriptions.filter(
            (subscription) =>
              subscription.isActive && subscription.subscriptionType === subscriptionTypeService.current,
          )

          const sortedSubs = activeSubs.sort((a, b) => {
            // put sub that has the latest start date first
            if (b.startDate > a.startDate) return 1
            if (b.startDate < a.startDate) return -1
            // if the same start date, put one with the further endDate
            else {
              if (b.endDate > a.endDate) return 1
              if (b.endDate < a.endDate) return -1
              // otherwsie return alphabetically
              return a.subscriptionName.localeCompare(b.subscriptionName)
            }
          })
          return sortedSubs[0].subscriptionName
        },
      },

      is3PRoster: {
        configurable: true,
        get() {
          return Boolean(sessionService.currentSchool.oneRosterEnabledAt)
        },
      },
    })

    window.walkme3P = window.walkme3P || {}

    Object.defineProperties(window.walkme3P, {
      countryCode: {
        configurable: true,
        get() {
          // WalkMe is expecting country codes as upper case
          return sessionService.currentSchool.countryCode
        },
      },
      isCleverManaged: {
        configurable: true,
        get() {
          return sessionService.currentSchool.externalRosteringSource === 'clever'
        },
      },
      isClassLinkManaged: {
        configurable: true,
        get() {
          return sessionService.currentSchool.externalRosteringSource === 'classlink'
        },
      },
      isPartnerManaged: {
        configurable: true,
        get() {
          const currentSubscription = subscriptionTypeService.current
          if (!currentSubscription) return false
          return !partnerService.hasPartner(PartnerCode._3P, currentSubscription)
        },
      },
      isTeacherTrial: {
        configurable: true,
        get() {
          return (
            subscriptionTypeService.current &&
            sessionService.currentTeacher.hasTrialSubscriptionFor(subscriptionTypeService.current)
          )
        },
      },
      isTrial: {
        configurable: true,
        get() {
          const currentSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) =>
              subscription.isActive && subscription.subscriptionType === subscriptionTypeService.current,
          )
          return currentSubscription?.isTrial ?? false
        },
      },
      partner: {
        configurable: true,
        get() {
          const currentSubscription = subscriptionTypeService.current

          if (!currentSubscription) return

          return partnerService.partnerForSubscriptionType(currentSubscription)?.code
        },
      },
      subscriptionType: {
        configurable: true,
        get() {
          return subscriptionTypeService.current
        },
      },
      userType: {
        configurable: true,
        get() {
          if (!subscriptionTypeService.current) return
          // WalkMe is expecting Teacher vs teacher
          return sessionService.currentTeacher.subcoFor(subscriptionTypeService.current) ? 'Subco' : 'Teacher'
        },
      },
      msUsCrossSell: {
        configurable: true,
        get() {
          return promoService.shouldShowMSUpsell
        },
      },
      readingSubscription: {
        configurable: true,
        get() {
          const readingSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) => subscription.subscriptionType === SubscriptionType.Reading && subscription.isActive,
          )
          return Boolean(readingSubscription)
        },
      },
      mathsSubscription: {
        configurable: true,
        get() {
          const mathsSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) => subscription.subscriptionType === SubscriptionType.Maths && subscription.isActive,
          )
          return Boolean(mathsSubscription)
        },
      },
      writingSubscription: {
        configurable: true,
        get() {
          const writingSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) => subscription.subscriptionType === SubscriptionType.Writing && subscription.isActive,
          )
          return Boolean(writingSubscription)
        },
      },
      mathleticsSubscription: {
        configurable: true,
        get() {
          const mathleticsSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) =>
              (subscription.subscriptionType as 'mathletics' | SubscriptionType) === 'mathletics' &&
              subscription.isActive,
          )
          return Boolean(mathleticsSubscription)
        },
      },
      readingTeacherTrial: {
        configurable: true,
        get() {
          return (
            sessionService.currentTeacher
              .teacherSubscriptionsFor(SubscriptionType.Reading)
              ?.some((sub) => sub.isActive) ?? false
          )
        },
      },
      readingTrial: {
        configurable: true,
        get() {
          const readingSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) => subscription.subscriptionType === SubscriptionType.Reading && subscription.isActive,
          )
          return readingSubscription?.isTrial ?? false
        },
      },
      mathsTeacherTrial: {
        configurable: true,
        get() {
          return (
            sessionService.currentTeacher
              .teacherSubscriptionsFor(SubscriptionType.Maths)
              ?.some((sub) => sub.isActive) ?? false
          )
        },
      },
      mathsTrial: {
        configurable: true,
        get() {
          const mathsSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) => subscription.subscriptionType === SubscriptionType.Maths && subscription.isActive,
          )
          return mathsSubscription?.isTrial ?? false
        },
      },
      writingTeacherTrial: {
        configurable: true,
        get() {
          return (
            sessionService.currentTeacher
              .teacherSubscriptionsFor(SubscriptionType.Writing)
              ?.some((sub) => sub.isActive) ?? false
          )
        },
      },
      writingTrial: {
        configurable: true,
        get() {
          const writingSubscription = sessionService.currentSchool.schoolSubscriptions.find(
            (subscription) => subscription.subscriptionType === SubscriptionType.Writing && subscription.isActive,
          )
          return writingSubscription?.isTrial ?? false
        },
      },
      schoolState: {
        configurable: true,
        get() {
          return sessionService.currentSchool.state
        },
      },

      promoSubscriptionName: {
        configurable: true,
        get() {
          const promoSubNames = ['ms_free_access_nov_24', 're_free_access_nov_24']

          const activeSubs = sessionService.currentSchool.schoolSubscriptions.filter(
            (subscription) => subscription.isActive,
          )

          // Return promo subscription names if active - regardless of current tab
          const promoSub = activeSubs.find((subscription) => promoSubNames.includes(subscription.subscriptionName))
          if (promoSub) return promoSub.subscriptionName
          return false
        },
      },

      currentSubscriptionName: {
        configurable: true,
        get() {
          const activeSubs = sessionService.currentSchool.schoolSubscriptions.filter(
            (subscription) =>
              subscription.isActive && subscription.subscriptionType === subscriptionTypeService.current,
          )

          const sortedSubs = sortSubscriptionsByStartDate(activeSubs)

          return sortedSubs.length ? sortedSubs[0].subscriptionName : undefined
        },
      },

      currentSubscriptionStartMonth: {
        configurable: true,
        get() {
          const activeSubs = sessionService.currentSchool.schoolSubscriptions.filter(
            (subscription) =>
              subscription.isActive && subscription.subscriptionType === subscriptionTypeService.current,
          )

          const sortedSubs = sortSubscriptionsByStartDate(activeSubs)

          return sortedSubs.length ? (sortedSubs[0].startDate.getMonth() + 1).toString() : undefined // getMonth is zero indexed
        },
      },
      currentSubscriptionStartDate: {
        configurable: true,
        get() {
          const activeSubs = sessionService.currentSchool.schoolSubscriptions.filter(
            (subscription) =>
              subscription.isActive && subscription.subscriptionType === subscriptionTypeService.current,
          )

          const sortedSubs = sortSubscriptionsByStartDate(activeSubs)

          if (!sortedSubs.length) return undefined

          const startDate = sortedSubs[0].startDate

          return format(startDate, 'M-yyyy')
        },
      },
      activeSubscriptions: {
        configurable: true,
        get() {
          const allSubTypes = [SubscriptionType.Reading, SubscriptionType.Maths, SubscriptionType.Writing]

          return allSubTypes.reduce<ActiveSubscriptions>((subscriptionsByType, subType) => {
            const forSubTypeActiveSubs = sessionService.currentSchool.schoolSubscriptions.filter(
              (subscription) => subscription.isActive && subscription.subscriptionType === subType,
            )
            const activeSubscriptionDateRange = getActiveSubscriptionDateRange(forSubTypeActiveSubs)

            if (activeSubscriptionDateRange) {
              subscriptionsByType[subType] = activeSubscriptionDateRange
            }

            return subscriptionsByType
          }, {})
        },
      },
      furthestOutSubscriptions: {
        configurable: true,
        get() {
          const allSubTypes = [SubscriptionType.Reading, SubscriptionType.Maths, SubscriptionType.Writing]

          return allSubTypes.reduce<ActiveSubscriptions>((subscriptionsByType, subType) => {
            const forSubTypeSubs = sessionService.currentSchool.schoolSubscriptions.filter(
              (subscription) =>
                (subscription.isActive || subscription.isPending) && subscription.subscriptionType === subType,
            )
            const furthestOutDateRange = getFurthestOutSubscriptionDateRange(forSubTypeSubs)

            if (furthestOutDateRange) {
              subscriptionsByType[subType] = furthestOutDateRange
            }

            return subscriptionsByType
          }, {})
        },
      },
      currentSubscriptionEndMonth: {
        configurable: true,
        get() {
          const activeSubs = sessionService.currentSchool.schoolSubscriptions.filter(
            (subscription) =>
              subscription.isActive && subscription.subscriptionType === subscriptionTypeService.current,
          )

          const sortedSubs = sortSubscriptionsByStartDate(activeSubs)

          return sortedSubs.length ? (sortedSubs[0].endDate.getMonth() + 1).toString() : undefined // getMonth is zero indexed
        },
      },

      is3PRoster: {
        configurable: true,
        get() {
          return Boolean(sessionService.currentSchool.oneRosterEnabledAt)
        },
      },
      districtName: {
        configurable: true,
        get() {
          return sessionService.currentSchool.district?.name
        },
      },
      reUserId: {
        configurable: true,
        get() {
          return sessionService.currentTeacher.id
        },
      },
    })
  }

  willDestroy(): void {
    super.willDestroy()

    delete window.countryCode
    delete window.isCleverManaged
    delete window.isPartnerManaged
    delete window.isTeacherTrial
    delete window.partner
    delete window.subscriptionType
    delete window.userType
    delete window.ms_us_cross_sell
    delete window.reading_subscription
    delete window.maths_subscription
    delete window.writing_subscription
    delete window.mathleticsSubscription
    delete window.reading_teacher_trial
    delete window.maths_teacher_trial
    delete window.writing_teacher_trial
    delete window.school_state
    delete window.promoSubscriptionName
    delete window.is3PRoster
    delete window.walkme3P
  }

  <template>
    {{#if this.shouldLoadWalkMe}}
      {{! template-lint-disable no-forbidden-elements }}
      <script type="text/javascript" async src={{this.loadScript}} data-test-walkme-script></script>
    {{/if}}
  </template>
}

export default WalkMe
